import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { IMAGES } from "../../data/site";

const LINES = ["ENGINEERING ERGONOMIC", "WORKSPACE SOLUTIONS", "— DIRECT FROM FACTORY."];

const lineWrap = {
    hidden: {},
    show: (i) => ({ transition: { staggerChildren: 0.14, delayChildren: 0.35 } }),
};
const line = {
    hidden: { y: "110%" },
    show: { y: "0%", transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 220]);
    const fade = useTransform(scrollY, [0, 500], [1, 0.35]);

    return (
        <section id="home" data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-navy flex flex-col">
            <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-[240px]">
                <img
                    src={IMAGES.hero}
                    alt="Modern corporate office workstations manufactured by Zam Zam Furnitures"
                    className="w-full h-full object-cover"
                    data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-navy/70" />
                <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/50 to-navy/20" />
                <div className="absolute inset-0 blueprint-grid" />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex flex-col justify-center max-w-[1400px] mx-auto w-full px-6 lg:px-10 pt-[140px] pb-24">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.7 }}
                    className="font-mono text-[11px] md:text-xs tracking-[0.35em] text-gold mb-8"
                    data-testid="hero-overline"
                >
                    ZAM ZAM FURNITURES · TASKLANCE® · ERRAGADDA, HYDERABAD
                </motion.p>

                <h1 className="font-heading font-extrabold tracking-tighter text-white text-[9.5vw] sm:text-6xl lg:text-7xl xl:text-[84px] leading-[1.02]" data-testid="hero-headline">
                    {LINES.map((text, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className={`block ${i === 2 ? "text-gold" : ""}`}
                                variants={line}
                                initial="hidden"
                                animate="show"
                                transition={{ delay: 0.3 + i * 0.14, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {text}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 0.8 }}
                    className="mt-8 max-w-xl text-slate-300 text-base md:text-lg leading-relaxed"
                    data-testid="hero-subheadline"
                >
                    Zam Zam Furnitures (TASKLANCE®) manufactures premium modular office workstations,
                    executive desks, ergonomic chairs, and custom storage solutions in Hyderabad.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.05, duration: 0.8 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <a
                        href="#products"
                        data-testid="explore-products-button"
                        className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-navy font-medium tracking-wide hover:bg-gold hover:text-white transition-colors duration-300"
                    >
                        Explore Products
                        <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </a>
                    <a
                        href="#contact"
                        data-testid="request-bulk-quote-button"
                        className="inline-flex items-center gap-2 px-8 py-4 border border-white/40 text-white font-medium tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
                    >
                        Request Bulk Quote
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 1 }}
                    className="mt-16 flex flex-wrap gap-x-10 gap-y-4 font-mono text-[11px] tracking-[0.25em] text-slate-400"
                    data-testid="hero-stats-strip"
                >
                    <span>1000+ PROJECTS DELIVERED</span>
                    <span>TS & AP INSTALLATION CREW</span>
                    <span>100% FACTORY DIRECT</span>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8 }}
                className="relative z-10 pb-8 flex justify-center"
            >
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
                    <ArrowDown size={18} className="text-gold" />
                </motion.div>
            </motion.div>
        </section>
    );
};
